import React, { useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatDateWithClock } from '~/services/TimeService';

import { getLatestSyncedAt } from '../../../CMChecklist/shared/checklistUtils';
import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';
import {
  DEFAULT_CURRENCY,
  formatDealOptionCurrency,
  formatDealOptionPercent,
} from '../dealOptions/dealOptionsFormatters';
import { getDropdownCellSx, handleExpandRowKeyDown, toggleExpandedRow } from '../surcharges/surchargeTabLayout';

import { countRoomRatesWithEgs, formatSlotList, getGuestSlot, groupEgsByRoom, isArchivedByName } from './egsUtils';
import type { EgsTabProps } from './types';

const EGS_TABLE_COLUMN_COUNT = 4;
const DETAIL_TABLE_COLUMN_COUNT = 9;

export default function EgsTab({
  error,
  extraGuestSurchargesByRoomRate,
  isLoading,
  isLoadingRoomRateEgs,
  roomRateEgsError,
  surcharges,
}: EgsTabProps) {
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set());
  const roomGroups = useMemo(() => groupEgsByRoom(surcharges), [surcharges]);
  const roomRatesWithEgs = useMemo(
    () => countRoomRatesWithEgs(extraGuestSurchargesByRoomRate),
    [extraGuestSurchargesByRoomRate],
  );
  const lastSyncedAt = useMemo(
    () => getLatestSyncedAt(surcharges.map((surcharge) => surcharge.synced_at)),
    [surcharges],
  );
  useDealInformationTabCount('egs', isLoading || error ? undefined : roomGroups.length);

  const subtitle = [
    `${roomGroups.length} room${roomGroups.length === 1 ? '' : 's'} in Salesforce`,
    roomRateEgsError ? 'Room rates in Reservations unavailable' : undefined,
    isLoadingRoomRateEgs || roomRateEgsError
      ? undefined
      : `${roomRatesWithEgs} room rate${roomRatesWithEgs === 1 ? '' : 's'} configured in Reservations`,
    lastSyncedAt ? `Last synced ${formatDateWithClock(lastSyncedAt)}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  const toggleRoom = (roomLabel: string) => setExpandedRooms((current) => toggleExpandedRow(current, roomLabel));

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading extra guest surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce extra guest surcharges.</Alert>;
  }

  return (
    <TabPanelShell title="Extra Guest Surcharges" subtitle={subtitle} contentSx={{ p: 0 }}>
      <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Typography color="text.secondary" variant="body2">
          Salesforce names each surcharge after the room it applies to, and holds one row per guest type within that
          room. Expand a room to see its adult, child, infant and teenager amounts.
        </Typography>
      </Box>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell />
              <TableCell>Room</TableCell>
              <TableCell>Guest types</TableCell>
              <TableCell align="center">Surcharges</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roomGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={EGS_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce extra guest surcharges found for this opportunity.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {roomGroups.map((group) => {
              const isExpanded = expandedRooms.has(group.roomLabel);

              return (
                <React.Fragment key={group.roomLabel}>
                  <TableRow
                    hover
                    role="button"
                    sx={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => toggleRoom(group.roomLabel)}
                    onKeyDown={(event) => handleExpandRowKeyDown(event, () => toggleRoom(group.roomLabel))}
                  >
                    <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">
                        {group.roomLabel || 'Unnamed'}
                      </Typography>
                      {group.conflictingSlots.length > 0 && (
                        <Typography color="warning.main" component="p" variant="caption">
                          More than one live row shares the {formatSlotList(group.conflictingSlots)} amount for this
                          room
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {group.slots.map((slot) => (
                          <Chip key={slot} label={slot} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={group.rows.length} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={EGS_TABLE_COLUMN_COUNT} sx={getDropdownCellSx(isExpanded)}>
                      <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
                        <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Guest type</TableCell>
                                <TableCell>Age range</TableCell>
                                <TableCell>Collected by</TableCell>
                                <TableCell>Bedding</TableCell>
                                <TableCell>Inclusions</TableCell>
                                <TableCell align="right">Cost</TableCell>
                                <TableCell align="right">Converted cost</TableCell>
                                <TableCell align="right">Sell price</TableCell>
                                <TableCell align="right">Margin</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.rows.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={DETAIL_TABLE_COLUMN_COUNT}>
                                    <Typography color="text.secondary" variant="body2">
                                      No surcharges for this room.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                              {group.rows.map((surcharge) => {
                                const slot = getGuestSlot(surcharge.extra_guest_type__c);

                                return (
                                  <TableRow key={surcharge.id}>
                                    <TableCell>
                                      <Stack alignItems="center" direction="row" spacing={1}>
                                        <Chip
                                          color={slot ? 'default' : 'warning'}
                                          label={surcharge.extra_guest_type__c || 'Unknown'}
                                          size="small"
                                          variant="outlined"
                                        />
                                        {isArchivedByName(surcharge.name) && (
                                          <Chip color="default" label="Archived" size="small" />
                                        )}
                                      </Stack>
                                    </TableCell>
                                    <TableCell>{surcharge.age_range__c || '—'}</TableCell>
                                    <TableCell>{surcharge.collected_by__c || '—'}</TableCell>
                                    <TableCell>
                                      {surcharge.other_extra_bedding_type__c || surcharge.bedding_type__c || '—'}
                                    </TableCell>
                                    <TableCell>{surcharge.inclusions__c || '—'}</TableCell>
                                    <TableCell align="right">
                                      {formatDealOptionCurrency(surcharge.cost_price_currency, surcharge.cost_price__c)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatDealOptionCurrency(DEFAULT_CURRENCY, surcharge.converted_cost__c)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatDealOptionCurrency(DEFAULT_CURRENCY, surcharge.sell_price_aud__c)}
                                    </TableCell>
                                    <TableCell align="right">{formatDealOptionPercent(surcharge.margin__c)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
